import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu.js';
import Button from '../ptcm/ui/Button.jsx';
import { useEmbeddedMode, withEmbeddedQuery } from '../ptcm/hooks/useEmbeddedMode.js';

const THEME_STORAGE_KEY = 'darkMode';

function getStoredThemePreference() {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'true') return 'dark';
  if (stored === 'false') return 'light';
  return null;
}

function Topbar({
  currentUser,
  logout,
  needsRecoveryEmail = false,
  recoveryEmailStatus = 'missing',
  onOpenRecoveryEmailPrompt = null
}) {
  const embedded = useEmbeddedMode();
  const [themePreference, setThemePreference] = useState(() => (embedded ? 'light' : getStoredThemePreference()));
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (embedded || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [isIframe, setIsIframe] = useState(false);
  const navigate = useNavigate();
  const darkMode = embedded
    ? false
    : themePreference === 'dark'
      ? true
      : themePreference === 'light'
        ? false
        : systemPrefersDark;

  useEffect(() => {
    // Detect if we're in an iframe
    setIsIframe(window !== window.parent);
  }, []);

  useEffect(() => {
    if (embedded || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      setSystemPrefersDark(event.matches);
    };

    setSystemPrefersDark(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [embedded]);

  useEffect(() => {
    if (embedded) {
      document.documentElement.classList.remove('dark');
      return undefined;
    }

    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode, embedded]);

  useEffect(() => {
    if (embedded || typeof window === 'undefined') return;

    if (themePreference === null) {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(THEME_STORAGE_KEY, String(themePreference === 'dark'));
  }, [themePreference, embedded]);

  const toggleDarkMode = () => {
    setThemePreference((previousPreference) => {
      const effectiveDarkMode = previousPreference === 'dark'
        ? true
        : previousPreference === 'light'
          ? false
          : systemPrefersDark;

      return effectiveDarkMode ? 'light' : 'dark';
    });
  };

  if (embedded) {
    return null;
  }

  // If we're in an iframe, show a minimal version
  if (isIframe) {
    return (
      <div className="fixed top-2 right-2 z-50">
        <UserMenu
          currentUser={currentUser}
          logout={logout}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          needsRecoveryEmail={needsRecoveryEmail}
          recoveryEmailStatus={recoveryEmailStatus}
          onOpenRecoveryEmailPrompt={onOpenRecoveryEmailPrompt}
        />
      </div>
    );
  }

  // Normal render outside iframe
  return (
    <div className="relative z-40 shrink-0 bg-ptcm-bg/85 dark:bg-gray-950/80 backdrop-blur-xl border-b border-ptcm-border/70 dark:border-gray-800 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div
            className="flex items-center gap-2 cursor-pointer group"
            onClick={() => navigate(withEmbeddedQuery('/', embedded))}
          >
            <div className="w-9 h-9 rounded-2xl bg-ptcm-primary flex items-center justify-center shadow-ptcm-float transition-all group-hover:-translate-y-[1px]">
              <span className="text-white font-bold text-lg leading-none">P</span>
            </div>
            <span className="font-display text-xl font-bold tracking-tight text-ptcm-ink dark:text-white transition-colors group-hover:text-ptcm-primary">
              Paye ta charge mentale !
            </span>
          </div>

          {/* Right side: User menu */}
          <div className="flex items-center gap-4">
            {needsRecoveryEmail && onOpenRecoveryEmailPrompt ? (
              <Button
                variant="outline"
                size="md"
                className={`hidden sm:inline-flex ${recoveryEmailStatus === 'pending'
                  ? 'border-amber-200/80 bg-amber-50/80 text-amber-800 hover:border-amber-300 hover:bg-amber-100/80 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200 dark:hover:bg-amber-900/30'
                  : 'border-red-200/80 bg-red-50/70 text-red-700 hover:border-red-300 hover:bg-red-100/80 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200 dark:hover:bg-red-900/30'}`}
                onClick={onOpenRecoveryEmailPrompt}
              >
                {recoveryEmailStatus === 'pending' ? 'Verifier mon email' : 'Enregistrer mon compte'}
              </Button>
            ) : null}
            <UserMenu
              currentUser={currentUser}
              logout={logout}
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
              needsRecoveryEmail={needsRecoveryEmail}
              recoveryEmailStatus={recoveryEmailStatus}
              onOpenRecoveryEmailPrompt={onOpenRecoveryEmailPrompt}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Topbar;